import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import {
  getProjectReportEmployees,
  getProjectTaskReport,
} from '../../services/projectReportService';
import {
  PROJECT_TASK_REPORT_CODE,
  type ProjectReportEmployeeOptions,
  type ProjectReportPeriod,
  type ProjectTaskReport,
  type ProjectTaskReportQuery,
  type ProjectTaskReportRow,
} from '../../types/projectReport';
import { formatCalendarDate, formatMinutes } from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  DEFAULT_PROJECT_REPORT_PERIOD,
  groupProjectReportByEmployee,
  PROJECT_REPORT_PERIOD_OPTIONS,
  projectReportPeriodLabel,
  validateProjectReportRange,
} from '../../utils/projectReport';
import type { ReportFileFormat } from '../../types/report';
import { reportErrorMessage } from '../../utils/reportErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectReport'>;

const ALL_EMPLOYEES = 'ALL';
const FILTER_PANE_WIDTH = 300;

function dateOrDash(value: string | null): string {
  return value ? formatCalendarDate(value) : '—';
}

function rangeLabel(fromDate: string, toDate: string): string {
  if (!fromDate || !toDate) return '';
  return fromDate === toDate
    ? formatCalendarDate(fromDate)
    : `${formatCalendarDate(fromDate)} – ${formatCalendarDate(toDate)}`;
}

function minutesOrZero(value: number): string {
  return value > 0 ? formatMinutes(value) : '0m';
}

function Detail({
  label,
  value,
  palette,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
}) {
  return (
    <Text style={[styles.detail, { color: palette.textPrimary }]}>
      <Text style={styles.detailLabel}>{label}: </Text>
      {value}
    </Text>
  );
}

function TaskCard({
  row,
  palette,
}: {
  row: ProjectTaskReportRow;
  palette: ColorPalette;
}) {
  const statusColor = row.isCompleted ? palette.success : palette.info;
  return (
    <View
      style={[styles.taskCard, { borderColor: palette.border }]}
      accessibilityLabel={`${row.taskName}, ${row.projectName}, status ${
        row.taskStatus ?? 'unknown'
      }`}
    >
      <View style={styles.taskHeader}>
        <View style={styles.taskTitleBlock}>
          <Text style={[styles.taskName, { color: palette.textPrimary }]}>
            {row.taskName}
          </Text>
          <Text style={[styles.caption, { color: palette.textSecondary }]}>
            {row.projectName}
          </Text>
        </View>
        {row.taskStatus ? (
          <View style={[styles.statusChip, { borderColor: statusColor }]}>
            <Text style={[styles.statusText, { color: statusColor }]}>
              {row.taskStatus}
            </Text>
          </View>
        ) : null}
      </View>
      <View style={[styles.dashedRow, { borderColor: palette.border }]}>
        <Detail label="Task date" value={dateOrDash(row.taskDate)} palette={palette} />
        <Detail label="Due date" value={dateOrDash(row.dueDate)} palette={palette} />
        <Detail
          label="Completion date"
          value={dateOrDash(row.completedDate)}
          palette={palette}
        />
      </View>
      <View style={[styles.dashedRow, { borderColor: palette.border }]}>
        <Detail
          label="Total spent"
          value={minutesOrZero(row.timeSpentMinutes)}
          palette={palette}
        />
        <Detail
          label="Time estimate"
          value={
            row.timeEstimateMinutes == null
              ? '—'
              : minutesOrZero(row.timeEstimateMinutes)
          }
          palette={palette}
        />
      </View>
      {row.subtaskTotal || row.lastWorkedOn || row.createdByName ? (
        <View style={styles.metaRow}>
          {row.subtaskTotal ? (
            <Text style={[styles.caption, { color: palette.textSecondary }]}>
              Subtasks {row.subtaskCompleted}/{row.subtaskTotal}
            </Text>
          ) : null}
          {row.lastWorkedOn ? (
            <Text style={[styles.caption, { color: palette.textSecondary }]}>
              Worked {rangeLabel(row.firstWorkedOn ?? row.lastWorkedOn, row.lastWorkedOn)}
              {row.timeLogCount ? ` · ${row.timeLogCount} log(s)` : ''}
            </Text>
          ) : null}
          {row.createdByName ? (
            <Text style={[styles.caption, { color: palette.textSecondary }]}>
              Created by {row.createdByName}
            </Text>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

export function ProjectReportScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const downloads = useReportDownloads();
  const { isWide } = useResponsiveLayout();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const projectId = route.params.projectId;

  const [employeeOptions, setEmployeeOptions] =
    useState<ProjectReportEmployeeOptions | null>(null);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [employeeValue, setEmployeeValue] = useState<string>(ALL_EMPLOYEES);
  const [period, setPeriod] = useState<ProjectReportPeriod>(
    DEFAULT_PROJECT_REPORT_PERIOD,
  );
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [report, setReport] = useState<ProjectTaskReport | null>(null);
  /** The exact query behind `report`; Download re-sends it so files match. */
  const [reportQuery, setReportQuery] = useState<ProjectTaskReportQuery | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  const rangeError =
    period === 'CUSTOM' && (fromDate || toDate)
      ? validateProjectReportRange(fromDate, toDate)
      : null;

  const query = useMemo<ProjectTaskReportQuery | null>(() => {
    if (!employeeOptions) return null;
    if (period === 'CUSTOM' && validateProjectReportRange(fromDate, toDate)) {
      return null;
    }
    return {
      period,
      fromDate: period === 'CUSTOM' ? fromDate : null,
      toDate: period === 'CUSTOM' ? toDate : null,
      employeeId: employeeValue === ALL_EMPLOYEES ? null : Number(employeeValue),
      projectId,
    };
  }, [employeeOptions, employeeValue, fromDate, period, projectId, toDate]);

  const loadEmployees = useCallback(async () => {
    setEmployeesError(null);
    try {
      const options = await getProjectReportEmployees(projectId);
      setEmployeeOptions(options);
      // Employees only ever see their own report: preselect themselves.
      if (!options.canSelectAllEmployees) {
        const self = options.items[0];
        setEmployeeValue(self ? String(self.employeeId) : ALL_EMPLOYEES);
      }
    } catch (err) {
      setEmployeesError(projectErrorMessage(err, 'Unable to load employees.'));
    }
  }, [projectId]);

  const canViewReport = capabilities.canViewTaskReport;
  useEffect(() => {
    if (canViewReport) loadEmployees();
  }, [canViewReport, loadEmployees]);

  const loadReport = useCallback(
    async (next: ProjectTaskReportQuery, mode: 'load' | 'refresh' = 'load') => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;
      if (mode === 'refresh') setRefreshing(true);
      else setLoading(true);
      setError(null);
      setDownloadNotice(null);
      try {
        const result = await getProjectTaskReport(next, controller.signal);
        if (controller.signal.aborted) return;
        setReport(result);
        setReportQuery(next);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(projectErrorMessage(err, 'Unable to load the task report.'));
      } finally {
        if (requestRef.current === controller) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [],
  );

  // Reload whenever the employee or date filters change.
  useEffect(() => {
    if (query) loadReport(query);
  }, [loadReport, query]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const employeeSelectOptions = useMemo(() => {
    const items = (employeeOptions?.items ?? []).map(employee => ({
      value: String(employee.employeeId),
      label: employee.employeeNumber
        ? `${employee.displayName} (${employee.employeeNumber})`
        : employee.displayName,
    }));
    return employeeOptions?.canSelectAllEmployees
      ? [{ value: ALL_EMPLOYEES, label: 'All Employees' }, ...items]
      : items;
  }, [employeeOptions]);

  const periodSelectOptions = useMemo(
    () =>
      PROJECT_REPORT_PERIOD_OPTIONS.map(option => ({
        value: option.value,
        label: option.label,
      })),
    [],
  );

  const sections = useMemo(
    () => (report ? groupProjectReportByEmployee(report) : []),
    [report],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.startsWith(`${PROJECT_TASK_REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const reportIsCurrent =
    Boolean(report && reportQuery && query) &&
    JSON.stringify(reportQuery) === JSON.stringify(query);
  const canDownload = reportIsCurrent && !loading && !activeProgress;

  const handleDownload = async (format: ReportFileFormat) => {
    if (!reportQuery || !report) return;
    setDownloadError(null);
    setDownloadNotice(null);
    try {
      const result = await downloads.startDownload({
        reportCode: PROJECT_TASK_REPORT_CODE,
        reportName: `${report.project.projectName} Task Report`,
        format,
        filters: {},
        projectTaskQuery: reportQuery,
      });
      if (Platform.OS === 'web') {
        setDownloadNotice(`Downloaded ${result.fileName}`);
        return;
      }
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(reportErrorMessage(err, 'The report could not be saved.'));
    }
  };

  if (!canViewReport) {
    return <UnauthorizedScreen />;
  }

  const subjectLabel = report?.filters.employeeName
    ? report.filters.employeeName
    : employeeOptions?.canSelectAllEmployees
      ? 'All Employees'
      : 'My Tasks';

  const filtersPane = (
    <View
      style={[
        styles.filtersPane,
        isWide ? { width: FILTER_PANE_WIDTH } : null,
        { borderColor: palette.border, backgroundColor: palette.surface },
      ]}
    >
      {employeesError ? (
        <ErrorState message={employeesError} onRetry={loadEmployees} />
      ) : (
        <RequestFormSelectField
          label="Employee"
          value={employeeValue}
          options={employeeSelectOptions}
          onChange={setEmployeeValue}
          placeholder={employeeOptions ? 'Select employee' : 'Loading employees…'}
          editable={Boolean(employeeOptions?.canSelectAllEmployees)}
        />
      )}
      <RequestFormSelectField
        label="Period"
        value={period}
        options={periodSelectOptions}
        onChange={value => setPeriod(value as ProjectReportPeriod)}
      />
      {period === 'CUSTOM' ? (
        <>
          <AppDateField label="From Date" value={fromDate} onChange={setFromDate} />
          <AppDateField
            label="To Date"
            value={toDate}
            onChange={setToDate}
            error={rangeError ?? undefined}
          />
        </>
      ) : null}
      <AppButton
        title="View Report"
        onPress={() => (query ? loadReport(query) : undefined)}
        disabled={!query || loading}
        loading={loading}
      />
      <AppButton
        title="Download PDF"
        variant="success"
        onPress={() => handleDownload('PDF')}
        disabled={!canDownload}
      />
      <View style={styles.downloadRow}>
        <AppButton
          title="Download Excel"
          variant="secondary"
          onPress={() => handleDownload('XLSX')}
          disabled={!canDownload}
          style={styles.downloadButton}
        />
        <AppButton
          title="Download CSV"
          variant="secondary"
          onPress={() => handleDownload('CSV')}
          disabled={!canDownload}
          style={styles.downloadButton}
        />
      </View>
      {activeProgress ? (
        <ReportDownloadProgressView
          progress={activeProgress}
          canCancel
          onCancel={() => downloads.cancelDownload(activeProgress.downloadId)}
        />
      ) : null}
      {downloadError ? (
        <Text style={[styles.message, { color: palette.error }]}>{downloadError}</Text>
      ) : null}
      {downloadNotice ? (
        <Text style={[styles.message, { color: palette.success }]}>
          {downloadNotice}
        </Text>
      ) : null}
    </View>
  );

  let reportBody: React.ReactNode;
  if (error) {
    reportBody = (
      <ErrorState message={error} onRetry={() => (query ? loadReport(query) : undefined)} />
    );
  } else if (!report && (loading || !employeeOptions)) {
    reportBody = <AppLoader message="Loading task report" />;
  } else if (!report) {
    reportBody = (
      <EmptyState
        title="Select a date range"
        message="Choose a From Date and To Date to view the report."
      />
    );
  } else {
    reportBody = (
      <View style={styles.reportContent}>
        <View>
          <Text style={[styles.reportTitle, { color: palette.textPrimary }]}>
            {report.project.projectName}
          </Text>
          <Text style={[styles.body, { color: palette.textSecondary }]}>
            Task Report
          </Text>
          <Text style={[styles.body, { color: palette.textSecondary }]}>
            {subjectLabel} · {projectReportPeriodLabel(report.filters.period)} ·{' '}
            {rangeLabel(report.filters.fromDate, report.filters.toDate)}
          </Text>
        </View>
        <View style={styles.metrics}>
          {[
            { label: 'Tasks', value: String(report.summary.taskCount) },
            { label: 'Completed', value: String(report.summary.completedCount) },
            {
              label: 'Time Spent',
              value: minutesOrZero(report.summary.spentMinutesInRange),
            },
            { label: 'Employees', value: String(report.summary.employeeCount) },
          ].map(metric => (
            <View
              key={metric.label}
              style={[styles.metric, { borderColor: palette.border }]}
            >
              <Text style={[styles.metricValue, { color: palette.textPrimary }]}>
                {metric.value}
              </Text>
              <Text style={[styles.caption, { color: palette.textSecondary }]}>
                {metric.label}
              </Text>
            </View>
          ))}
        </View>
        {loading ? (
          <Text style={[styles.caption, { color: palette.textMuted }]}>
            Updating…
          </Text>
        ) : null}
        {sections.length === 0 ? (
          <EmptyState
            title="No tasks in this period"
            message="No task was worked on, completed or created in the selected range."
          />
        ) : (
          sections.map(section => (
            <View key={section.employeeId} style={styles.section}>
              <Text style={[styles.sectionTitle, { color: palette.textPrimary }]}>
                {section.employeeName}
                {section.employeeNumber ? ` (${section.employeeNumber})` : ''}
                {` — ${section.rows.length} task${section.rows.length === 1 ? '' : 's'}`}
              </Text>
              <Text style={[styles.caption, { color: palette.textSecondary }]}>
                Completed {section.completedCount} · Time spent{' '}
                {minutesOrZero(section.spentMinutesInRange)}
              </Text>
              {section.rows.map(row => (
                <TaskCard
                  key={`${row.employeeId}:${row.taskId}`}
                  row={row}
                  palette={palette}
                />
              ))}
            </View>
          ))
        )}
      </View>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen
        scroll
        webLayout="wide"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => (query ? loadReport(query, 'refresh') : undefined)}
          />
        }
      >
        <ScreenHeader
          title="Task Report"
          subtitle={report?.project.projectName ?? route.params.projectName ?? null}
          onBack={navigation.goBack}
        />
        <View style={[styles.layout, isWide ? styles.layoutWide : null]}>
          {filtersPane}
          <View
            style={[
              styles.reportPane,
              { borderColor: palette.border, backgroundColor: palette.surface },
            ]}
          >
            {reportBody}
          </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  layout: { marginTop: spacing.lg, gap: spacing.lg },
  layoutWide: { flexDirection: 'row', alignItems: 'flex-start' },
  filtersPane: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.lg,
    gap: spacing.md,
  },
  reportPane: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.lg,
  },
  reportContent: { gap: spacing.lg },
  reportTitle: { ...typography.screenTitle },
  body: { ...typography.body },
  caption: { ...typography.caption },
  message: { ...typography.caption },
  downloadRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  downloadButton: { flexGrow: 1, flexBasis: 120 },
  metrics: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  metric: {
    flexGrow: 1,
    flexBasis: 110,
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.xs,
  },
  metricValue: { ...typography.sectionTitle },
  section: { gap: spacing.sm },
  sectionTitle: { ...typography.sectionTitle },
  taskCard: {
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.sm,
  },
  taskHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  taskTitleBlock: { flex: 1, gap: 2 },
  taskName: { ...typography.body, fontWeight: '600' },
  statusChip: {
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  statusText: { ...typography.caption, fontWeight: '600' },
  dashedRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: spacing.lg,
    rowGap: spacing.xs,
    borderTopWidth: 1,
    borderStyle: 'dashed',
    paddingTop: spacing.sm,
  },
  detail: { ...typography.caption },
  detailLabel: { fontWeight: '700' },
  metaRow: { flexDirection: 'row', flexWrap: 'wrap', columnGap: spacing.lg },
});
